/**
 * FprContext
 *
 * FPR-game-specific state owned outside GameContext: failed scoring tracking,
 * admin retry/force-complete actions, and the admin Results snapshot (with
 * debounced refresh on relevant broadcasts).
 *
 * Mounted by GameTypeProvider only when the selected game is an FPR game.
 *
 * Note: FPR_POOLS_* listeners stay in GameContext because they update the
 * universal Game object. This context only owns FPR-only state that no other
 * part of the dashboard cares about.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { onGameStateUpdated, useSocketContext } from '@swtorbit/shared-react-hooks';
import {
  Game,
  SocketEvent,
  GameStateUpdatedBroadcast,
  FprScoringFailedBroadcast,
  FprAdminRetryScoringRequest,
  FprAdminRetryScoringResponse,
  FprAdminForceCompleteRoundRequest,
  FprAdminForceCompleteRoundResponse,
  AdminGetFprResultsRequest,
  AdminFprResultsResponse,
} from '@swtorbit/shared-socket-events';
import { gameLogger } from '../utils/logger';
import { useGameContext } from './GameContext';

interface FprContextType {
  failedScoringRounds: Set<number>;
  retryFprScoring: (
    request: FprAdminRetryScoringRequest,
    callback?: (resp: FprAdminRetryScoringResponse) => void
  ) => void;
  forceCompleteFprRound: (
    request: FprAdminForceCompleteRoundRequest,
    callback?: (resp: FprAdminForceCompleteRoundResponse) => void
  ) => void;
  fprAdminResults: AdminFprResultsResponse | null;
  refreshFprAdminResults: () => void;
}

const FprContext = createContext<FprContextType | undefined>(undefined);

interface FprProviderProps {
  children: ReactNode;
}

export function FprProvider({ children }: FprProviderProps) {
  const { socket } = useSocketContext();
  const { selectedGame } = useGameContext();

  const [failedScoringRounds, setFailedScoringRounds] = useState<Set<number>>(new Set());
  const [fprAdminResults, setFprAdminResults] = useState<AdminFprResultsResponse | null>(null);
  const fprResultsRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedGameRef = useRef<Game | null>(selectedGame);
  useEffect(() => {
    selectedGameRef.current = selectedGame;
  }, [selectedGame]);

  // Clear FPR-specific state when switching games
  useEffect(() => {
    setFailedScoringRounds(new Set());
    setFprAdminResults(null);
  }, [selectedGame?.dbId]);

  const retryFprScoring = useCallback(
    (
      request: FprAdminRetryScoringRequest,
      callback?: (resp: FprAdminRetryScoringResponse) => void
    ) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(SocketEvent.FPR_ADMIN_RETRY_SCORING, request, (resp: FprAdminRetryScoringResponse) => {
        callback?.(resp);
      });
    },
    [socket]
  );

  const forceCompleteFprRound = useCallback(
    (
      request: FprAdminForceCompleteRoundRequest,
      callback?: (resp: FprAdminForceCompleteRoundResponse) => void
    ) => {
      if (!socket) {
        callback?.({ success: false, error: 'Not connected' });
        return;
      }
      socket.emit(SocketEvent.FPR_ADMIN_FORCE_COMPLETE_ROUND, request, (resp: FprAdminForceCompleteRoundResponse) => {
        callback?.(resp);
      });
    },
    [socket]
  );

  const refreshFprAdminResults = useCallback(() => {
    const game = selectedGameRef.current;
    if (!game || !socket) return;
    const req: AdminGetFprResultsRequest = { gameId: game.dbId };
    socket.emit(SocketEvent.FPR_ADMIN_GET_RESULTS, req);
  }, [socket]);

  const scheduleFprAdminResultsRefresh = useCallback(() => {
    if (fprResultsRefreshTimer.current) {
      clearTimeout(fprResultsRefreshTimer.current);
    }
    fprResultsRefreshTimer.current = setTimeout(() => {
      fprResultsRefreshTimer.current = null;
      refreshFprAdminResults();
    }, 250);
  }, [refreshFprAdminResults]);

  useEffect(() => {
    if (!socket) return;

    // Prune failed-scoring entries for any round that is no longer 'racing'
    // (retry/force-complete/cancel/end all clear the failure state).
    const handleRoundUpdated = (broadcast: GameStateUpdatedBroadcast) => {
      if (broadcast.game.id !== selectedGameRef.current?.id) return;
      // Only react to round-lifecycle sources
      const roundSources = new Set([
        'round_started', 'round_ended', 'round_cancelled', 'round_disabled',
        'round_edited', 'round_loaded', 'round_unloaded', 'round_force_results_toggled',
      ]);
      if (!roundSources.has(broadcast.source)) return;
      const stillRacingIndexes = new Set<number>(
        broadcast.game.rounds
          .map((r, idx) => (r.status === 'racing' ? idx : -1))
          .filter((idx) => idx >= 0)
      );
      setFailedScoringRounds(prev => {
        let changed = false;
        const next = new Set<number>();
        for (const idx of prev) {
          if (stillRacingIndexes.has(idx)) next.add(idx);
          else changed = true;
        }
        return changed ? next : prev;
      });
    };

    const handleScoringFailed = (broadcast: FprScoringFailedBroadcast) => {
      if (broadcast.roomCode !== selectedGameRef.current?.roomCode) return;
      if (broadcast.roundIndex < 0) {
        gameLogger.warn('FPR scoring failed but roundIndex missing', { broadcast });
        return;
      }
      gameLogger.warn('FPR scoring failed — surfacing to admin', { broadcast });
      setFailedScoringRounds(prev => {
        if (prev.has(broadcast.roundIndex)) return prev;
        const next = new Set(prev);
        next.add(broadcast.roundIndex);
        return next;
      });
    };

    const handleAdminFprResults = (response: AdminFprResultsResponse) => {
      if (response.gameId !== selectedGameRef.current?.dbId) return;
      setFprAdminResults(response);
    };

    // Live race broadcasts; an archived game's results never change.
    const handleResultsRelevantBroadcast = () => {
      if (!selectedGameRef.current || selectedGameRef.current.archived) return;
      scheduleFprAdminResultsRefresh();
    };

    const offGameState = onGameStateUpdated(socket, handleRoundUpdated);
    socket.on(SocketEvent.FPR_SCORING_FAILED, handleScoringFailed);
    socket.on(SocketEvent.FPR_ADMIN_RESULTS, handleAdminFprResults);
    socket.on(SocketEvent.FPR_PROGRESS_UPDATE, handleResultsRelevantBroadcast);
    socket.on(SocketEvent.FPR_BOSS_KILLED_BROADCAST, handleResultsRelevantBroadcast);
    socket.on(SocketEvent.FPR_TEAM_STARTED_BROADCAST, handleResultsRelevantBroadcast);
    socket.on(SocketEvent.FPR_TEAM_FINISHED_BROADCAST, handleResultsRelevantBroadcast);
    socket.on(SocketEvent.FPR_TEAM_DNF_BROADCAST, handleResultsRelevantBroadcast);
    socket.on(SocketEvent.FPR_ALL_TEAMS_FINISHED, handleResultsRelevantBroadcast);

    return () => {
      offGameState();
      socket.off(SocketEvent.FPR_SCORING_FAILED, handleScoringFailed);
      socket.off(SocketEvent.FPR_ADMIN_RESULTS, handleAdminFprResults);
      socket.off(SocketEvent.FPR_PROGRESS_UPDATE, handleResultsRelevantBroadcast);
      socket.off(SocketEvent.FPR_BOSS_KILLED_BROADCAST, handleResultsRelevantBroadcast);
      socket.off(SocketEvent.FPR_TEAM_STARTED_BROADCAST, handleResultsRelevantBroadcast);
      socket.off(SocketEvent.FPR_TEAM_FINISHED_BROADCAST, handleResultsRelevantBroadcast);
      socket.off(SocketEvent.FPR_TEAM_DNF_BROADCAST, handleResultsRelevantBroadcast);
      socket.off(SocketEvent.FPR_ALL_TEAMS_FINISHED, handleResultsRelevantBroadcast);
      if (fprResultsRefreshTimer.current) {
        clearTimeout(fprResultsRefreshTimer.current);
        fprResultsRefreshTimer.current = null;
      }
    };
  }, [socket, scheduleFprAdminResultsRefresh]);

  const value = useMemo(() => ({
    failedScoringRounds,
    retryFprScoring,
    forceCompleteFprRound,
    fprAdminResults,
    refreshFprAdminResults,
  }), [
    failedScoringRounds,
    retryFprScoring,
    forceCompleteFprRound,
    fprAdminResults,
    refreshFprAdminResults,
  ]);

  return <FprContext.Provider value={value}>{children}</FprContext.Provider>;
}

export function useFprContext() {
  const context = useContext(FprContext);
  if (context === undefined) {
    throw new Error('useFprContext must be used within an FprProvider');
  }
  return context;
}
